import { Cross2Icon } from "@radix-ui/react-icons";
import { Box, Button, Select, Spinner, Text } from "@radix-ui/themes";
import { Control, Controller, FieldErrors, FieldValues, Path } from "react-hook-form";
import styles from "./SelectComponent.module.scss";

interface SelectProps<T extends FieldValues> {
  name: Path<T>; // Agora restrito apenas a chaves de `T`
  label: string;
  control: Control<T>;
  errors: FieldErrors<T>;
  style?: any;
  fieldProps?: any;
  options?: { value: string; label: string }[];
  itemsLoading?: boolean;
  itemsError?: boolean;
  onSelect?: (value: string) => void;
  composedName?: { parent: any; prop: any };
  required?: boolean;
}

const SelectComponent = <T extends FieldValues>({
  name,
  label,
  control,
  errors,
  style,
  fieldProps,
  options,
  onSelect,
  composedName,
  itemsError,
  itemsLoading,
  required,
}: SelectProps<T>) => {
  const castedErrors = errors as Record<string, any>;

  const composedError =
    composedName && composedName.parent && composedName.prop
      ? castedErrors?.[composedName.parent]?.[composedName.prop]
      : undefined;

  return (
    <Box {...style}>
      <Text as="label" size="2" className={styles.label}>
        <span>
          {label} {required && <span style={{ color: "red" }}>*</span>}
        </span>
      </Text>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <>
            <div className={styles.selectContainer}>
              <Select.Root
                onValueChange={(value) => {
                  field.onChange(value);
                  if (onSelect) onSelect(value);
                }}
                value={field.value || ""}
                {...fieldProps}
              >
                {itemsLoading ? (
                  <Spinner loading />
                ) : itemsError ? (
                  <Text>Erro ao carregar items</Text>
                ) : (
                  <>
                    <Select.Trigger style={{ width: "100%", position: "relative" }}>
                      {options?.find((p) => p.value === field.value)?.label || "Selecione uma opção"}
                    </Select.Trigger>
                    {field.value && !fieldProps?.disabled && (
                      <Button
                        variant="ghost"
                        size="1"
                        onClick={() => field.onChange("")}
                        className={styles.clearButton}
                        style={{ margin: "4px 0px 0px 0px" }}
                      >
                        <Cross2Icon />
                      </Button>
                    )}
                    <Select.Content>
                      <Select.Group>
                        <Select.Label>Opções</Select.Label>
                        {options?.map((option) => (
                          <Select.Item key={option.value} value={option.value}>
                            {option.label}
                          </Select.Item>
                        ))}
                      </Select.Group>
                    </Select.Content>
                  </>
                )}
              </Select.Root>
            </div>
            {composedError ? (
              <Text color="red" size="1">
                {composedError.message as string}
              </Text>
            ) : (
              errors?.[name] && (
                <Text color="red" size="1">
                  {errors?.[name]?.message as string}
                </Text>
              )
            )}
          </>
        )}
      />
    </Box>
  );
};

export default SelectComponent;
